import React, { useCallback, useEffect, useState } from 'react';
import { useForm, Resolver } from 'react-hook-form';
import {
  defaultDisabledFeatureConfigs,
  defaultEnabledFeatureConfigs,
  initialSchoolConfigData
} from './constants';
import { yupResolver } from '@hookform/resolvers/yup';
import { schoolConfigValidationSchema } from './validationSchema';
import { SnackbarSeverityType } from '../../../enums';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../redux/store';
import { fetchSchoolByName } from '../../../redux/slices/School';
import {
  fetchSchoolClassesConfig,
  fetchSchoolConfig
} from '../../../redux/slices/SchoolConfigSlice';
import { initialSchoolConfigProps } from '../types';
import { updateSchoolConfig } from '../../../apis/schoolConfig';
import { uploadImageToS3 } from '../../../apis/uploadToS3';
import formUtils from '../../../utils/formUtils';
export const useOrgSettingsForm = () => {
  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors }
  } = useForm<initialSchoolConfigProps, any, initialSchoolConfigProps>({
    resolver:
      (yupResolver(
        schoolConfigValidationSchema
      ) as unknown as Resolver<initialSchoolConfigProps>) || undefined,
    mode: 'onChange',
    defaultValues:
      initialSchoolConfigData as unknown as initialSchoolConfigProps
  });

  const formData = watch();

  const dispatch: AppDispatch = useDispatch();

  const [expandedSection, setExpandedSection] = useState<string | false>(
    'general'
  );

  const [snackbar, setSnackbar] = useState({
    show: false,
    severity: SnackbarSeverityType.success,
    message: ''
  });

  const { user: loggedInUser } = useSelector(
    (state: RootState) => state.profile
  );
  const { schoolConfig, loadingStatus: loadingSchoolConfigData } = useSelector(
    (state: RootState) => state.schoolConfig
  );

  const { singleSchool } = useSelector((state: RootState) => state.school);

  useEffect(() => {
    if (loggedInUser?.school) {
      dispatch(fetchSchoolByName(loggedInUser?.school));
      dispatch(fetchSchoolConfig(loggedInUser?.school));
      dispatch(fetchSchoolClassesConfig(loggedInUser?.school));
    }
  }, [loggedInUser?.school, dispatch]);

  useEffect(() => {
    if (schoolConfig) {
      const arrayKeysToProcess = ['schoolInfo'];
      const processedSchoolConfig = formUtils.addUniqueIdsToArrayItems(
        schoolConfig,
        arrayKeysToProcess
      ) as initialSchoolConfigProps;
      reset(processedSchoolConfig);
    }
  }, [schoolConfig, reset]);

  const handleAccordionChange = useCallback(
    (section: string) => (_: React.ChangeEvent<{}>, isExpanded: boolean) => {
      setExpandedSection(isExpanded ? section : false);
    },
    []
  );

  const handleFileUpload = useCallback(
    async (file: File, uploadFor: string): Promise<string> => {
      if (!loggedInUser?.school) {
        throw new Error('User school information is not available.');
      }
      const url = await uploadImageToS3({
        file,
        uploadFor: uploadFor,
        fileName: file.name,
        school: loggedInUser.school
      });
      return url;
    },
    [loggedInUser?.school]
  );

  const applyDefaultFeatureConfigs = useCallback(
    (currentFormData: any) => {
      const newFeatureConfigs = { ...currentFormData.featureConfigs };

      if (!formData.id) {
        defaultEnabledFeatureConfigs.forEach((key) => {
          newFeatureConfigs[key] = {
            ...newFeatureConfigs[key],
            enabled: true
          };
        });

        defaultDisabledFeatureConfigs.forEach((key) => {
          newFeatureConfigs[key] = {
            ...newFeatureConfigs[key],
            enabled: false
          };
        });
      }

      return {
        ...currentFormData,
        featureConfigs: newFeatureConfigs
      };
    },
    [formData.id]
  );

  const processRoleFeaturesMapping = (roleFeaturesMapping: any[]) => {
    return roleFeaturesMapping.map((roleFeature: any) => {
      if (roleFeature.role !== 'teacher' || !roleFeature.features)
        return roleFeature;

      const { publishDcu, addRemarksDcu } = roleFeature.features;

      const shouldEnable = (metaData: any) =>
        !!metaData &&
        (metaData.subjectTeacher?.enabled || metaData.classTeacher?.enabled);

      if (publishDcu) {
        if (shouldEnable(publishDcu.metaData)) {
          publishDcu.enabled = true;
        } else {
          publishDcu.enabled = false;
        }
      }

      if (addRemarksDcu) {
        if (shouldEnable(addRemarksDcu.metaData)) {
          addRemarksDcu.enabled = true;
        } else {
          addRemarksDcu.enabled = false;
        }
      }

      return roleFeature;
    });
  };

  const transformFormData = useCallback(
    (data: any) => {
      const { gradeSectionMapping, ...rest } = data;
      let transformedData = applyDefaultFeatureConfigs({ ...rest });

      const retoolUser = transformedData.functionalUserEmails.retoolUser;

      if (transformedData.functionalUserEmails) {
        transformedData = {
          ...transformedData,
          functionalUserEmails: {
            ...transformedData.functionalUserEmails,
            retoolUser:
              retoolUser === ''
                ? `${singleSchool.shortCode}+retool@${transformedData.studentEmaildDomain}`
                : retoolUser
          }
        };
      }

      if (Array.isArray(transformedData.roleFeaturesMapping)) {
        transformedData.roleFeaturesMapping = processRoleFeaturesMapping(
          transformedData.roleFeaturesMapping
        );
      }

      return transformedData;
    },
    [applyDefaultFeatureConfigs, singleSchool?.shortCode]
  );

  const onSubmit = useCallback(
    async (data: any) => {
      const updatedFormData = transformFormData(data);

      try {
        const response = await updateSchoolConfig(
          loggedInUser?.school,
          updatedFormData
        );
        if (response)
          setSnackbar({
            show: true,
            severity: SnackbarSeverityType.success,
            message: 'School updated successfully!'
          });
      } catch (error: any) {
        setSnackbar({
          show: true,
          severity: SnackbarSeverityType.error,
          message:
            error.errors?.[0]?.message ??
            'Failed to update school. Please try again.'
        });
      }
    },
    [transformFormData, loggedInUser?.school]
  );

  const onError = useCallback((formErrors: typeof errors) => {
    const firstFormError = formUtils.findFirstFormError(formErrors);
    setExpandedSection(firstFormError?.fieldPath.split('.')[0] || '');

    setSnackbar({
      show: true,
      severity: SnackbarSeverityType.error,
      message:
        firstFormError?.errorMessage ||
        (firstFormError?.fieldPath
          ? `Please correct the error in "${firstFormError.fieldPath}".`
          : 'Please correct the errors in the form.')
    });
  }, []);

  return {
    control,
    expandedSection,
    loadingSchoolConfigData,
    snackbar,
    userRole: 'superAdmin',
    handleAccordionChange,
    handleSubmit: handleSubmit(onSubmit, onError),
    reset,
    setValue,
    handleFileUpload,
    setSnackbar
  };
};
